import type { LogicConfig, LogicResult, MultiAxisScoringConfig } from '../types';
import type { QuestionValue } from '@lib/types/survey';
// Adjust import path for weightsStore 
// We'll need to set up proper importing between packages - for now comment it out
// import { weightsStore } from '@survey-player/lib/stores/weightsStore';

/**
 * Process multi-axis scoring logic configuration
 * @param config The multi-axis scoring logic configuration
 * @param value The current question value (selected option key)
 */
export async function processMultiAxisScoringLogic(
  config: LogicConfig,
  value: QuestionValue
): Promise<LogicResult> {
  // If no value is selected, return standard progression
  if (!value) {
    return {
      success: true,
      nextAction: {
        type: 'standard',
        value: 0
      }
    };
  }

  const multiAxisConfig = config.config as MultiAxisScoringConfig;
  
  try {
    // For now, we'll emit an event that the survey-player app can listen for
    // This avoids direct dependencies between the packages
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('multiAxisScoring', {
        detail: {
          value,
          config: multiAxisConfig,
          logicConfig: config
        }
      });
      window.dispatchEvent(event);
    }
    
    return {
      success: true,
      nextAction: {
        type: 'standard',
        value: 0
      },
      metadata: {
        scoringApplied: true
      }
    };
  } catch (error) {
    console.error('Error processing multi-axis scoring logic:', error);
    
    return {
      success: false,
      nextAction: {
        type: 'standard',
        value: 0
      },
      metadata: {
        error: 'Failed to process multi-axis scoring'
      }
    };
  }
}

/**
 * Processes multi-axis scoring for a question and formats weights
 * Can be used in the survey-player to update the weightsStore
 * 
 * @param ctx The context object containing screen and value information
 * @param getQuestionId A function to get the question ID from a screen
 * @returns The same context object, potentially modified
 */
export async function processMultiAxisScoring(
  ctx: any, 
  getQuestionId: (screen: any, index: number) => string
): Promise<any> {
  if (!ctx.screen || 
      !('type' in ctx.screen) || 
      ctx.screen.type !== 'question' || 
      !ctx.value) {
    return ctx;
  }

  try {
    // Check if the screen has weights
    if (!('weights' in ctx.screen) || !ctx.screen.weights) {
      return ctx;
    }
    
    // Get weights configuration from the question
    const weights = ctx.screen.weights || [];
    
    // Get the questionId
    const questionId = getQuestionId(ctx.screen, ctx.index);
    
    // Format weights for the store
    const formattedWeights = formatWeightsForStore(weights);
    
    // Return the formatted weights with the context
    return {
      ...ctx,
      multiAxisScoring: {
        questionId,
        value: ctx.value,
        formattedWeights
      }
    };
  } catch (error) {
    // Silent error handling
    return ctx;
  }
}

/**
 * Sets up an event listener for the multiAxisScoring event
 * This abstracts away the event handling from the page component
 * 
 * @param getQuestionId Function to get question IDs
 * @param getCurrentScreen Function to get the current screen
 * @param getCurrentIndex Function to get the current index
 * @param addWeights Function to add weights to a store
 * @returns Cleanup function to remove the event listener
 */
export function setupMultiAxisScoringListener(
  getQuestionId: (screen: any, index: number) => string,
  getCurrentScreen: () => any,
  getCurrentIndex: () => number,
  addWeights: (questionId: string, value: any, weights: any[]) => void
): () => void {
  // Create the event handler function
  const handleMultiAxisScoring = ((event: CustomEvent) => {
    const currentScreen = getCurrentScreen();
    const currentIndex = getCurrentIndex();
    
    if (!currentScreen) return;
    
    const { value } = event.detail;
    
    // Check if current screen has weights
    if (currentScreen && 
        'type' in currentScreen && 
        currentScreen.type === 'question' && 
        'weights' in currentScreen && 
        currentScreen.weights) {
      
      // Format weights properly for the store
      const rawWeights = currentScreen.weights;
      const formattedWeights = formatWeightsForStore(rawWeights);
      
      // Add the weights to the store
      addWeights(
        getQuestionId(currentScreen, currentIndex),
        value,
        formattedWeights
      );
    }
  }) as EventListener;
  
  // Add the event listener
  if (typeof window !== 'undefined') {
    window.addEventListener('multiAxisScoring', handleMultiAxisScoring);
  }
  
  // Return a cleanup function
  return () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('multiAxisScoring', handleMultiAxisScoring);
    }
  };
}

/**
 * Helper function to format weights for the store
 * @param rawWeights The raw weights from the screen
 * @returns Formatted weights for the store
 */
export function formatWeightsForStore(rawWeights: any[]): any[] {
  return Array.isArray(rawWeights) ? rawWeights.map(weight => {
    if (weight && typeof weight === 'object' && 'key' in weight && 'axes' in weight) {
      // If 'axes' is already an object with axis ids as keys, use it directly
      if (typeof weight.axes === 'object' && weight.axes !== null) {
        return weight;
      }
    } 
    
    if (weight && typeof weight === 'object' && 'key' in weight) {
      // Remove the key from the object to create the axes object
      const { key, ...rest } = weight;
      
      // If there's an 'axes' property, use it
      if ('axes' in rest && typeof rest.axes === 'object' && rest.axes !== null) {
        return { key, axes: rest.axes };
      }
      
      // Otherwise, use all remaining properties as axes
      return { 
        key, 
        axes: rest 
      };
    }
    
    return { key: '__INVALID__', axes: {} };
  }) : [];
} 